import { useCallback, useEffect, useState } from 'react';
import { marketInternalRadarAPI } from '../../services/api';

export function useMarketInternalRadarDashboard({ t, language }) {
  const locale = (language || '').toLowerCase().startsWith('vi') ? 'vi-VN' : 'en-US';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [candidatesPayload, setCandidatesPayload] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState({
    tradeDate: '',
    minScore: 50,
    rankLabel: '',
    sector: '',
    validatedOnly: false,
    historyFreq: 'D',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    tradeDate: '',
    minScore: 50,
    rankLabel: '',
    sector: '',
    validatedOnly: false,
    historyFreq: 'D',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tradeDate = appliedFilters.tradeDate || null;
      const [summaryRes, candidatesRes, sectorsRes, historyRes] = await Promise.all([
        marketInternalRadarAPI.getSummary(tradeDate),
        marketInternalRadarAPI.getCandidates({
          trade_date: tradeDate,
          min_score: Number(appliedFilters.minScore) || 50,
          rank_label: appliedFilters.rankLabel || undefined,
          sector: appliedFilters.sector || undefined,
          validated_only: appliedFilters.validatedOnly,
          limit: 50,
          offset: 0,
        }),
        marketInternalRadarAPI.getSectors(tradeDate, 20),
        marketInternalRadarAPI.getHistory(appliedFilters.historyFreq, 30),
      ]);
      setSummary(summaryRes.data);
      setCandidatesPayload(candidatesRes.data);
      setSectors(sectorsRes.data?.sectors || []);
      setHistory(historyRes.data?.rows || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message || t('marketInternalRadar.errors.load', 'Failed to load Market Internal Radar data'));
      setCandidatesPayload(null);
      setSectors([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters.historyFreq, appliedFilters.minScore, appliedFilters.rankLabel, appliedFilters.sector, appliedFilters.tradeDate, appliedFilters.validatedOnly, t]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      await marketInternalRadarAPI.refresh({
        trade_date: appliedFilters.tradeDate || undefined,
        persist: true,
      });
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message || t('marketInternalRadar.errors.refresh', 'Failed to refresh Market Internal Radar'));
    } finally {
      setRefreshing(false);
    }
  }, [appliedFilters.tradeDate, load, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters });
  }, [filters]);

  return {
    locale,
    loading,
    refreshing,
    error,
    summary,
    candidatesPayload,
    sectors,
    history,
    filters,
    setFilters,
    handleFilterChange,
    load,
    applyFilters,
    refresh,
  };
}
